/**
 * TurnstileWidget — Cloudflare Turnstile challenge widget (SCRUM-183).
 *
 * Uso:
 *   <TurnstileWidget onToken={(token) => setToken(token)} onError={() => setError(true)} />
 *
 * El componente carga el script de Cloudflare de forma lazy (una sola vez por página)
 * y renderiza el challenge. Cuando el usuario lo resuelve, llama a `onToken` con el token
 * que luego debe enviarse como `cfTurnstileToken` en el body de la petición al servidor.
 */

import { useEffect, useRef, useCallback } from "react";
import { TURNSTILE_SITE_KEY } from "@/lib/turnstile-public";

interface TurnstileWidgetProps {
  /** Callback cuando el usuario completa el challenge exitosamente */
  onToken: (token: string) => void;
  /** Callback cuando el widget falla o expira */
  onError?: () => void;
  /** Callback cuando el token expira (el usuario debe resolver de nuevo) */
  onExpire?: () => void;
  /** Tema visual del widget */
  theme?: "light" | "dark" | "auto";
  /** Clase CSS adicional para el contenedor */
  className?: string;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_ID = "cf-turnstile-script";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      // Script ya existe → esperar a que Turnstile esté listo
      if (window.turnstile) {
        resolve();
      } else {
        // Pooling simple: el script ya está cargando
        const check = setInterval(() => {
          if (window.turnstile) {
            clearInterval(check);
            resolve();
          }
        }, 50);
        setTimeout(() => {
          clearInterval(check);
          reject(new Error("Turnstile script timeout"));
        }, 10_000);
      }
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(script);
  });
}

export function TurnstileWidget({
  onToken,
  onError,
  onExpire,
  theme = "auto",
  className,
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const handleToken = useCallback((token: string) => onToken(token), [onToken]);
  const handleError = useCallback(() => onError?.(), [onError]);
  const handleExpire = useCallback(() => onExpire?.(), [onExpire]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        await loadTurnstileScript();
        if (cancelled || !containerRef.current || !window.turnstile) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: handleToken,
          "error-callback": handleError,
          "expired-callback": handleExpire,
          theme,
        });
      } catch {
        handleError();
      }
    }

    void init();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignorar
        }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
}
